import { withFirebaseAuthorization } from './authFetch';

async function parseJson(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

function normalizeApiError(response, payload) {
  return {
    status: response.status,
    error:
      payload?.error ||
      payload?.message ||
      `Request failed with status ${response.status}.`,
    requestId: payload?.requestId || payload?.data?.requestId,
  };
}

export function createWorkspaceClient(baseUrl, fetchImpl = fetch) {
  const normalizedBaseUrl = baseUrl.replace(/\/$/, '');
  const userBaseUrl = (userId) =>
    `${normalizedBaseUrl}/api/users/${encodeURIComponent(userId)}`;
  const normalizeScope = (scope) => (scope === 'family' ? 'family' : 'personal');
  const listBaseUrl = (userId, scope = 'personal') =>
    `${userBaseUrl(userId)}${normalizeScope(scope) === 'family' ? '/family' : ''}/lists`;
  const taskBaseUrl = (userId, scope = 'personal') =>
    `${userBaseUrl(userId)}${normalizeScope(scope) === 'family' ? '/family/tasks' : '/tasks'}`;
  const eventBaseUrl = (userId, scope = 'personal') =>
    `${userBaseUrl(userId)}${
      normalizeScope(scope) === 'family' ? '/family/calendar/events' : '/calendar/events'
    }`;

  async function request(url, options = {}) {
    const response = await fetchImpl(url, await withFirebaseAuthorization(options));
    const payload = await parseJson(response);

    if (!response.ok) {
      throw normalizeApiError(response, payload);
    }

    return payload;
  }

  return {
    getLists(userId) {
      return request(`${userBaseUrl(userId)}/lists`);
    },

    getListItems(userId, listId, { status, scope = 'personal' } = {}) {
      const query = new URLSearchParams();
      if (status && status !== 'all') {
        query.set('status', status);
      }

      return request(
        `${listBaseUrl(userId, scope)}/${encodeURIComponent(listId)}/items${
          query.toString() ? `?${query}` : ''
        }`
      );
    },

    createList(userId, list) {
      const { scope = 'personal', ...payload } = list || {};
      return request(listBaseUrl(userId, scope), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    },

    updateList(userId, listId, updates, { scope = 'personal' } = {}) {
      const { scope: _scope, ...payload } = updates || {};
      return request(`${listBaseUrl(userId, scope)}/${encodeURIComponent(listId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
    },

    deleteList(userId, listId, { scope = 'personal' } = {}) {
      return request(`${listBaseUrl(userId, scope)}/${encodeURIComponent(listId)}/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
    },

    createListItem(userId, listId, item, { scope = 'personal' } = {}) {
      return request(`${listBaseUrl(userId, scope)}/${encodeURIComponent(listId)}/items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: [item || {}] }),
      });
    },

    updateListItem(userId, listId, itemId, updates, { scope = 'personal' } = {}) {
      return request(
        `${listBaseUrl(userId, scope)}/${encodeURIComponent(listId)}/items/${encodeURIComponent(
          itemId
        )}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updates || {}),
        }
      );
    },

    deleteListItem(userId, listId, itemId, { scope = 'personal' } = {}) {
      return request(
        `${listBaseUrl(userId, scope)}/${encodeURIComponent(listId)}/items/${encodeURIComponent(
          itemId
        )}/delete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );
    },

    getTasks(userId, filters = {}) {
      const query = new URLSearchParams();
      if (filters.status && filters.status !== 'all') query.set('status', filters.status);
      if (filters.assignedTo) query.set('assignedTo', filters.assignedTo);
      return request(
        `${userBaseUrl(userId)}/tasks${query.toString() ? `?${query}` : ''}`
      );
    },

    createTask(userId, task) {
      const { scope = 'personal', ...payload } = task || {};
      return request(taskBaseUrl(userId, scope), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tasks: [payload] }),
      });
    },

    updateTask(userId, taskId, updates, { scope = 'personal' } = {}) {
      const { scope: _scope, ...payload } = updates || {};
      return request(`${taskBaseUrl(userId, scope)}/${encodeURIComponent(taskId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
    },

    completeTask(userId, taskId, { scope = 'personal' } = {}) {
      return request(
        `${taskBaseUrl(userId, scope)}/${encodeURIComponent(taskId)}/complete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );
    },

    deleteTask(userId, taskId, { scope = 'personal' } = {}) {
      return request(
        `${taskBaseUrl(userId, scope)}/${encodeURIComponent(taskId)}/delete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );
    },

    getEvents(userId, filters = {}) {
      const query = new URLSearchParams();
      if (filters.status && filters.status !== 'all') query.set('status', filters.status);
      if (filters.startFrom) query.set('startFrom', filters.startFrom);
      if (filters.startTo) query.set('startTo', filters.startTo);
      return request(
        `${userBaseUrl(userId)}/calendar/events${query.toString() ? `?${query}` : ''}`
      );
    },

    createEvent(userId, event) {
      const { scope = 'personal', ...payload } = event || {};
      return request(eventBaseUrl(userId, scope), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events: [payload] }),
      });
    },

    updateEvent(userId, eventId, updates, { scope = 'personal' } = {}) {
      const { scope: _scope, ...payload } = updates || {};
      return request(`${eventBaseUrl(userId, scope)}/${encodeURIComponent(eventId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
    },

    deleteEvent(userId, eventId, { scope = 'personal' } = {}) {
      return request(`${eventBaseUrl(userId, scope)}/${encodeURIComponent(eventId)}/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
    },

    getReminders(userId, { view } = {}) {
      const query = new URLSearchParams();
      if (view && view !== 'all') query.set('view', view);
      return request(
        `${userBaseUrl(userId)}/reminders${query.toString() ? `?${query}` : ''}`
      );
    },

    getDailyBriefing(userId, filters = {}) {
      const query = new URLSearchParams();
      if (filters.date) query.set('date', filters.date);
      if (filters.scope && filters.scope !== 'auto') query.set('scope', filters.scope);
      if (filters.mode && filters.mode !== 'today') query.set('mode', filters.mode);

      return request(
        `${userBaseUrl(userId)}/day/briefing${query.toString() ? `?${query}` : ''}`
      );
    },

    completeReminder(userId, listId, itemId, { scope = 'personal' } = {}) {
      return this.updateListItem(
        userId,
        listId,
        itemId,
        { status: 'completed' },
        { scope }
      );
    },

    getNotifications(userId, { status = 'all', limit = 50 } = {}) {
      const query = new URLSearchParams();
      if (status && status !== 'all') {
        query.set('status', status);
      }
      if (typeof limit === 'number' && Number.isFinite(limit)) {
        query.set('limit', String(Math.max(1, Math.floor(limit))));
      }
      return request(
        `${userBaseUrl(userId)}/notifications${query.toString() ? `?${query}` : ''}`
      );
    },

    markNotificationRead(userId, notificationId) {
      return request(
        `${userBaseUrl(userId)}/notifications/${encodeURIComponent(notificationId)}/read`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );
    },

    markNotificationUnread(userId, notificationId) {
      return request(
        `${userBaseUrl(userId)}/notifications/${encodeURIComponent(notificationId)}/unread`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );
    },

    searchContext(userId, question) {
      return request(`${userBaseUrl(userId)}/context-search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question,
        }),
      });
    },
  };
}
